import React, { useState, useEffect } from "react";
import {
  Code,
  Globe,
  Share2,
  CheckCircle,
  AlertCircle,
  Copy,
  ExternalLink,
  Sparkles,
  Save,
  RotateCcw,
  Check,
  HelpCircle,
  Layers,
  FileCode,
  UploadCloud,
  Download,
} from "lucide-react";
import { DEFAULT_SEO_SETTINGS } from "../../api";
import { useToast } from "../../hooks/use-toast";
import CsvUploadModal from "../../components/admin/CsvUploadModal";
import { toCsv, downloadCsv } from "../../utils/csvHelper";

const SchemaSettingsEditor = ({ settings, onSave }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(settings || DEFAULT_SEO_SETTINGS);
  const [activePreview, setActivePreview] = useState("website");
  const [copied, setCopied] = useState(false);
  const [jsonError, setJsonError] = useState("");
  const [saving, setSaving] = useState(false);
  const [csvModalOpen, setCsvModalOpen] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm({ ...DEFAULT_SEO_SETTINGS, ...settings });
    }
  }, [settings]);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleCustomJsonChange = (val) => {
    setField("customHeadJsonLd", val);
    if (!val || val.trim() === "") {
      setJsonError("");
      return;
    }
    try {
      JSON.parse(val);
      setJsonError("");
    } catch (e) {
      setJsonError(e.message);
    }
  };

  const handleReset = () => {
    if (window.confirm("Reset all Schema and SEO settings to default recommended values?")) {
      setForm(DEFAULT_SEO_SETTINGS);
      setJsonError("");
    }
  };

  const handleExportCsv = () => {
    const rows = Object.entries(form)
      .filter(([k]) => k !== "updatedAt")
      .map(([k, v]) => ({ setting: k, value: typeof v === "object" ? JSON.stringify(v) : String(v) }));
    const cols = [
      { key: "setting", label: "Setting Key" },
      { key: "value", label: "Setting Value" },
    ];
    const csv = toCsv(rows, cols);
    downloadCsv("seo_schema_settings.csv", csv);
    toast({ title: "SEO Settings Exported", description: `Exported ${rows.length} settings to CSV.` });
  };

  const handleImportCsv = async (validItems, rawRows, mode) => {
    const updates = {};
    validItems.forEach((item) => {
      const k = item.setting;
      let val = item.value;
      if (val === "true" || val === "TRUE" || val === "1") val = true;
      else if (val === "false" || val === "FALSE" || val === "0") val = false;
      if (k in DEFAULT_SEO_SETTINGS || k in form) {
        updates[k] = val;
      }
    });

    setForm((prev) => ({
      ...(mode === "replace" ? DEFAULT_SEO_SETTINGS : prev),
      ...updates,
    }));

    toast({
      title: "SEO Settings Loaded",
      description: `Loaded ${Object.keys(updates).length} settings from CSV. Click 'Save Settings' to apply.`,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (jsonError) {
      alert("Please fix the JSON syntax error in Custom JSON-LD before saving.");
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  // Generate real-time schema preview based on current form values
  const getPreviewSchema = () => {
    const base = form.siteUrl || "https://minigolfalabama.com";
    const name = form.siteName || "Mini Golf USA";

    switch (activePreview) {
      case "website":
        return {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: name,
          alternateName: ["Mini Golf Alabama", "Mini Golf Near Me", "Putt Putt Directory"],
          url: base,
          description: form.defaultDescription,
          potentialAction: {
            "@type": "SearchAction",
            target: {
              "@type": "EntryPoint",
              urlTemplate: `${base}/state/alabama?q={search_term_string}`,
            },
            "query-input": "required name=search_term_string",
          },
        };
      case "organization":
        const socialList = [
          form.socialFacebook,
          form.socialTwitter,
          form.socialInstagram,
          form.socialYoutube,
        ].filter(Boolean);
        return {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: name,
          url: base,
          logo: form.logoUrl || `${base}/logo.png`,
          sameAs: socialList,
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "Customer Support",
            email: form.contactEmail || "contact@minigolfalabama.com",
            telephone: form.contactPhone || undefined,
            availableLanguage: ["English"],
          },
        };
      case "course":
        return {
          "@context": "https://schema.org",
          "@type": ["SportsActivityLocation", "AmusementPark", "LocalBusiness"],
          name: "Pirate's Island Adventure Golf",
          image: form.defaultOgImage || `${base}/logo.png`,
          description: "Explore 18 holes of family miniature golf adventures with pirate caves and waterfalls.",
          telephone: "(251) 555-0199",
          url: `${base}/state/alabama/course/pirates-island-adventure-golf`,
          priceRange: "$10 - $15 per round",
          address: {
            "@type": "PostalAddress",
            streetAddress: "24000 Perdido Beach Blvd",
            addressLocality: "Orange Beach",
            addressRegion: "AL",
            addressCountry: "US",
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: 4.8,
            reviewCount: 142,
            bestRating: 5,
            worstRating: 1,
          },
        };
      case "faq":
        return {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Are reservations required for mini golf?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Most mini golf courses operate on a walk-in basis, but large groups and birthday parties can reserve ahead.",
              },
            },
            {
              "@type": "Question",
              name: "Are putters and golf balls provided?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes, standard admission includes your choice of putter size and colored golf ball.",
              },
            },
          ],
        };
      case "breadcrumbs":
        return {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${base}/` },
            { "@type": "ListItem", position: 2, name: "Alabama", item: `${base}/state/alabama` },
            { "@type": "ListItem", position: 3, name: "Orange Beach", item: `${base}/state/alabama/orange-beach` },
          ],
        };
      case "collection":
        return {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Best Mini Golf Courses in Alabama",
          description: "Directory of indoor and outdoor mini golf courses in Alabama.",
          url: `${base}/state/alabama`,
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: 12,
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Pirate's Island Adventure Golf",
                url: `${base}/state/alabama/course/pirates-island-adventure-golf`,
              },
            ],
          },
        };
      case "blog":
        return {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: "The Ultimate Guide to Glow-in-the-Dark Mini Golf",
          description: "Discover the best neon blacklight mini golf courses for family fun and night adventures.",
          image: `${base}/logo.png`,
          datePublished: "2026-03-01T12:00:00Z",
          dateModified: "2026-03-05T15:30:00Z",
          author: { "@type": "Person", name: "Mini Golf Editorial Team" },
          publisher: {
            "@type": "Organization",
            name: name,
            logo: { "@type": "ImageObject", url: form.logoUrl || `${base}/logo.png` },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": `${base}/blog/glow-in-the-dark-mini-golf-guide` },
        };
      default:
        return {};
    }
  };

  const previewJsonString = JSON.stringify(getPreviewSchema(), null, 2);

  const handleCopy = () => {
    navigator.clipboard.writeText(previewJsonString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const inputCls =
    "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white";

  return (
    <div className="space-y-8" data-testid="schema-settings-editor">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-emerald-800 via-teal-900 to-gray-900 rounded-2xl p-6 sm:p-8 text-white shadow-lg">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-xs font-semibold mb-3">
              <Sparkles className="w-3.5 h-3.5" /> Schema.org & SEO Engine
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              Schema & Search Engine Structured Data
            </h2>
            <p className="text-sm text-emerald-100/80 mt-1 max-w-2xl">
              Configure Schema.org JSON-LD markup, Google Rich Results snippets, Open Graph social tags, and site-wide metadata to maximize search visibility.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleExportCsv}
              title="Export all SEO & Schema settings to CSV"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-emerald-100 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button
              type="button"
              onClick={() => setCsvModalOpen(true)}
              title="Import SEO & Schema settings from Excel, CSV, TSV, or JSON"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-gray-900 bg-white hover:bg-gray-100 rounded-lg shadow-sm transition-colors"
            >
              <UploadCloud className="w-3.5 h-3.5" /> Import (Excel / CSV)
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-emerald-200 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset Defaults
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-gray-900 bg-emerald-400 hover:bg-emerald-300 rounded-lg shadow-md transition-all disabled:opacity-50"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Settings"}
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Section 1: Site Identity & Global Meta */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-2.5 border-b border-gray-100 pb-4 mb-6">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <Globe className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">1. Site Identity & Canonical Configuration</h3>
              <p className="text-xs text-gray-500">Root URL and general metadata used for default search snippets.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Site Brand Name</label>
              <input
                type="text"
                className={inputCls}
                value={form.siteName || ""}
                onChange={(e) => setField("siteName", e.target.value)}
                placeholder="e.g. Mini Golf USA"
              />
              <p className="text-[11px] text-gray-400 mt-1">Used in schema `name` and title suffix.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Base Canonical URL</label>
              <input
                type="url"
                className={inputCls}
                value={form.siteUrl || ""}
                onChange={(e) => setField("siteUrl", e.target.value)}
                placeholder="https://minigolfalabama.com"
              />
              <p className="text-[11px] text-gray-400 mt-1">Search engines use this as the primary canonical domain.</p>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Default Meta Title</label>
              <input
                type="text"
                className={inputCls}
                value={form.defaultTitle || ""}
                onChange={(e) => setField("defaultTitle", e.target.value)}
                placeholder="Mini Golf Alabama | Best Indoor & Outdoor Mini Golf Near Me"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Default Meta Description</label>
              <textarea
                rows={2}
                className={inputCls}
                value={form.defaultDescription || ""}
                onChange={(e) => setField("defaultDescription", e.target.value)}
                placeholder="Find a fun and exciting mini golf putt putt course near me..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Default Social / Open Graph Image URL</label>
              <input
                type="url"
                className={inputCls}
                value={form.defaultOgImage || ""}
                onChange={(e) => setField("defaultOgImage", e.target.value)}
                placeholder="https://minigolfalabama.com/logo.png"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Organization Logo URL</label>
              <input
                type="url"
                className={inputCls}
                value={form.logoUrl || ""}
                onChange={(e) => setField("logoUrl", e.target.value)}
                placeholder="https://minigolfalabama.com/logo.png"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Google Analytics 4 (GA4) Measurement ID</label>
              <input
                type="text"
                className={inputCls}
                value={form.googleAnalyticsId || ""}
                onChange={(e) => setField("googleAnalyticsId", e.target.value)}
                placeholder="e.g. G-XXXXXXXXXX"
              />
              <p className="text-[11px] text-gray-400 mt-1">Enables automatic pageview tracking across all route transitions.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Google Search Console Verification Code</label>
              <input
                type="text"
                className={inputCls}
                value={form.googleSiteVerification || ""}
                onChange={(e) => setField("googleSiteVerification", e.target.value)}
                placeholder="e.g. bi4Dgit8tyad33aIxnsWe3v0F_ufmeXoIziR7ybwYjU"
              />
              <p className="text-[11px] text-gray-400 mt-1">Injected into document head for Google Search Console verification.</p>
            </div>
          </div>
        </div>

        {/* Section 2: Organization Schema & Social Graph */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-2.5 border-b border-gray-100 pb-4 mb-6">
            <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <Share2 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">2. Organization Schema & Social Graph (`sameAs`)</h3>
              <p className="text-xs text-gray-500">
                Powers Knowledge Graph panels on Google and embeds verified brand connections.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Support Email</label>
              <input
                type="email"
                className={inputCls}
                value={form.contactEmail || ""}
                onChange={(e) => setField("contactEmail", e.target.value)}
                placeholder="contact@minigolfalabama.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Support Phone</label>
              <input
                type="text"
                className={inputCls}
                value={form.contactPhone || ""}
                onChange={(e) => setField("contactPhone", e.target.value)}
                placeholder="(800) 555-GOLF"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Facebook Profile URL</label>
              <input
                type="url"
                className={inputCls}
                value={form.socialFacebook || ""}
                onChange={(e) => setField("socialFacebook", e.target.value)}
                placeholder="https://facebook.com/..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Twitter / X Profile URL</label>
              <input
                type="url"
                className={inputCls}
                value={form.socialTwitter || ""}
                onChange={(e) => setField("socialTwitter", e.target.value)}
                placeholder="https://twitter.com/..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Instagram Profile URL</label>
              <input
                type="url"
                className={inputCls}
                value={form.socialInstagram || ""}
                onChange={(e) => setField("socialInstagram", e.target.value)}
                placeholder="https://instagram.com/..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">YouTube Channel URL</label>
              <input
                type="url"
                className={inputCls}
                value={form.socialYoutube || ""}
                onChange={(e) => setField("socialYoutube", e.target.value)}
                placeholder="https://youtube.com/..."
              />
            </div>
          </div>
        </div>

        {/* Section 3: Schema Types Toggles */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-2.5 border-b border-gray-100 pb-4 mb-6">
            <div className="w-9 h-9 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">3. Schema.org Types Enablement</h3>
              <p className="text-xs text-gray-500">Toggle individual Schema.org structured data types injected across site routes.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                id: "enableWebsiteSchema",
                label: "WebSite & SearchAction Schema",
                desc: "Enables Google Sitelinks search box & directory name indexing on Homepage.",
              },
              {
                id: "enableOrganizationSchema",
                label: "Organization Schema",
                desc: "Attaches brand logo, contact points, and social profiles to About and Contact pages.",
              },
              {
                id: "enableCourseSchema",
                label: "SportsActivityLocation & LocalBusiness Schema",
                desc: "Injects venue coordinates, address, opening hours, star ratings, and prices on course pages.",
              },
              {
                id: "enableCollectionSchema",
                label: "CollectionPage & ItemList Schema",
                desc: "Categorizes course listings into structured collections on State and City destination pages.",
              },
              {
                id: "enableFaqSchema",
                label: "FAQPage Schema",
                desc: "Generates rich FAQ accordion snippets in search result lists for state, city, and course pages.",
              },
              {
                id: "enableBreadcrumbSchema",
                label: "BreadcrumbList Schema",
                desc: "Provides clear hierarchical navigation paths in Google search results (Home > State > City > Course).",
              },
              {
                id: "enableBlogSchema",
                label: "BlogPosting Article Schema",
                desc: "Delivers rich article snippets with author, publishing timestamps, and featured imagery.",
              },
            ].map((t) => (
              <label
                key={t.id}
                className="flex items-start gap-3 p-3.5 rounded-xl border border-gray-100 bg-gray-50 hover:bg-emerald-50/50 hover:border-emerald-200 transition-all cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={form[t.id] !== false}
                  onChange={(e) => setField(t.id, e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                />
                <div>
                  <div className="text-sm font-semibold text-gray-900">{t.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{t.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Section 4: Custom Global JSON-LD Injector */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-6">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                <FileCode className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">4. Custom Global JSON-LD Injector</h3>
                <p className="text-xs text-gray-500">
                  Optional: Paste raw custom Schema.org JSON structured data to inject site-wide into document head.
                </p>
              </div>
            </div>
            {jsonError ? (
              <span className="inline-flex items-center gap-1 text-xs text-red-600 font-semibold bg-red-50 px-2.5 py-1 rounded-md">
                <AlertCircle className="w-3.5 h-3.5" /> Invalid JSON
              </span>
            ) : form.customHeadJsonLd ? (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold bg-emerald-50 px-2.5 py-1 rounded-md">
                <CheckCircle className="w-3.5 h-3.5" /> Valid JSON
              </span>
            ) : null}
          </div>

          <textarea
            rows={5}
            className="w-full px-3.5 py-3 font-mono text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-gray-900 text-emerald-400 placeholder:text-gray-500"
            value={form.customHeadJsonLd || ""}
            onChange={(e) => handleCustomJsonChange(e.target.value)}
            placeholder={`{\n  "@context": "https://schema.org",\n  "@type": "WebSite",\n  "name": "Custom Schema"\n}`}
          />
          {jsonError && (
            <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> Syntax Error: {jsonError}
            </p>
          )}
        </div>

        {/* Section 5: Live Schema Inspector & Preview */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-100 pb-4 mb-6">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <Code className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">5. Real-Time Schema.org JSON-LD Inspector</h3>
                <p className="text-xs text-gray-500">
                  Inspect the live JSON-LD code generated by your settings and test in Google Rich Results Test.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="https://search.google.com/test/rich-results"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Google Rich Results Test
              </a>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied!" : "Copy JSON"}
              </button>
            </div>
          </div>

          {/* Schema Type Switcher */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
            {[
              { id: "website", label: "WebSite & SearchAction" },
              { id: "organization", label: "Organization" },
              { id: "course", label: "SportsActivityLocation (Course)" },
              { id: "collection", label: "CollectionPage (State/City)" },
              { id: "faq", label: "FAQPage" },
              { id: "breadcrumbs", label: "BreadcrumbList" },
              { id: "blog", label: "BlogPosting" },
            ].map((p) => (
              <button
                type="button"
                key={p.id}
                onClick={() => setActivePreview(p.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${
                  activePreview === p.id
                    ? "bg-gray-900 text-emerald-400 font-bold"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Code Viewer */}
          <div className="relative rounded-xl overflow-hidden bg-gray-950 border border-gray-800 p-4">
            <div className="flex items-center justify-between text-[11px] text-gray-400 border-b border-gray-800 pb-2 mb-3">
              <span className="font-mono text-emerald-400">
                &lt;script type="application/ld+json"&gt;
              </span>
              <span className="text-gray-500">Schema.org JSON-LD</span>
            </div>
            <pre className="font-mono text-xs text-emerald-300 overflow-x-auto leading-relaxed max-h-72">
              {previewJsonString}
            </pre>
          </div>
        </div>

        {/* Sticky Action Footer */}
        <div className="sticky bottom-4 z-20 bg-white/95 backdrop-blur-md rounded-xl border border-gray-200 shadow-xl p-4 flex items-center justify-between">
          <p className="text-xs text-gray-500 flex items-center gap-1.5">
            <HelpCircle className="w-4 h-4 text-emerald-600" />
            Changes take effect immediately across all live pages upon saving.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleExportCsv}
              className="px-3 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button
              type="button"
              onClick={() => setCsvModalOpen(true)}
              title="Import SEO & Schema settings from Excel, CSV, TSV, or JSON"
              className="px-3 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors flex items-center gap-1.5"
            >
              <UploadCloud className="w-3.5 h-3.5" /> Import (Excel / CSV)
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-500 rounded-lg shadow transition-all disabled:opacity-50"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving Changes..." : "Save Schema Settings"}
            </button>
          </div>
        </div>
      </form>

      <CsvUploadModal
        isOpen={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        sectionKey="schema"
        onImport={handleImportCsv}
        allowModeSwitch={true}
      />
    </div>
  );
};

export default SchemaSettingsEditor;
